import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import clsx from "clsx";

import {
  Button,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ScheduleModal from "../../components/ScheduleModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  body: {
    maxWidth: 360,
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  status: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.common.white,
    whiteSpace: "nowrap",
  },
  pending: { backgroundColor: theme.palette.primary.main },
  sending: { backgroundColor: theme.palette.warning.main },
  sent: { backgroundColor: theme.palette.success.main },
  error: { backgroundColor: theme.palette.error.main },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },
}));

const FILTERS = ["pending", "sent", "error", "all"];

const Schedules = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchSchedules = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const { data } = await api.get("/schedules", {
          params: {
            status: filter === "all" ? undefined : filter,
            searchParam: search || undefined,
            pageNumber: page,
          },
        });
        setSchedules((prev) =>
          page === 1 ? data.schedules : [...prev, ...data.schedules]
        );
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    },
    [filter, search]
  );

  useEffect(() => {
    setPageNumber(1);
    const timer = setTimeout(() => fetchSchedules(1), 300);
    return () => clearTimeout(timer);
  }, [fetchSchedules]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("schedule", (data) => {
      if (data.action === "delete") {
        setSchedules((prev) => prev.filter((s) => s.id !== data.scheduleId));
        return;
      }
      const { schedule } = data;
      setSchedules((prev) => {
        const others = prev.filter((s) => s.id !== schedule.id);
        // Some da lista quando o status deixa de bater com o filtro
        if (filter !== "all" && schedule.status !== filter) return others;
        const exists = others.length !== prev.length;
        return exists
          ? prev.map((s) => (s.id === schedule.id ? schedule : s))
          : [schedule, ...prev];
      });
    });

    return () => socket.disconnect();
  }, [filter]);

  const canChange = (schedule) =>
    ["pending", "error"].includes(schedule.status) &&
    (user.profile === "admin" || schedule.userId === user.id);

  const handleDelete = async () => {
    try {
      await api.delete(`/schedules/${selected.id}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setSelected(null);
  };

  const handleLoadMore = () => {
    const next = pageNumber + 1;
    setPageNumber(next);
    fetchSchedules(next);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("schedules.confirmationModal.deleteTitle")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ScheduleModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelected(null);
        }}
        scheduleId={selected?.id}
      />
      <MainHeader>
        <Title>{i18n.t("schedules.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelected(null);
              setModalOpen(true);
            }}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.toolbar}>
        <Tabs
          value={filter}
          onChange={(e, value) => setFilter(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          {FILTERS.map((f) => (
            <Tab key={f} value={f} label={i18n.t(`schedules.filters.${f}`)} />
          ))}
        </Tabs>
        <TextField
          size="small"
          variant="outlined"
          placeholder={i18n.t("schedules.filters.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("schedules.table.contact")}</TableCell>
              <TableCell>{i18n.t("schedules.table.body")}</TableCell>
              <TableCell align="center">{i18n.t("schedules.table.sendAt")}</TableCell>
              <TableCell align="center">{i18n.t("schedules.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("schedules.table.user")}</TableCell>
              <TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule) => (
              <TableRow key={schedule.id}>
                <TableCell>{schedule.contact?.name}</TableCell>
                <TableCell data-label={i18n.t("schedules.table.body")}>
                  <Typography variant="body2" className={classes.body}>
                    {schedule.body}
                  </Typography>
                </TableCell>
                <TableCell align="center" data-label={i18n.t("schedules.table.sendAt")}>
                  {format(parseISO(schedule.sendAt), "dd/MM/yyyy HH:mm")}
                </TableCell>
                <TableCell align="center" data-label={i18n.t("schedules.table.status")}>
                  <Tooltip
                    title={
                      schedule.status === "sent" && schedule.sentAt
                        ? format(parseISO(schedule.sentAt), "dd/MM/yyyy HH:mm")
                        : schedule.errorMessage || ""
                    }
                  >
                    <span className={clsx(classes.status, classes[schedule.status])}>
                      {i18n.t(`schedules.status.${schedule.status}`)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" data-label={i18n.t("schedules.table.user")}>
                  {schedule.user?.name || "-"}
                </TableCell>
                <TableCell align="center">
                  {canChange(schedule) && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelected(schedule);
                          setModalOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelected(schedule);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </ResponsiveTable>
        {!loading && schedules.length === 0 && (
          <div className={classes.empty}>{i18n.t("schedules.empty")}</div>
        )}
        {hasMore && !loading && (
          <div className={classes.loadMore}>
            <Button onClick={handleLoadMore}>{i18n.t("schedules.loadMore")}</Button>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Schedules;
